"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useTranslations } from "next-intl";

import { Link } from "@/i18n/navigation";
import { Button } from "@repo/ui/components/base/button";
import { Card, CardContent } from "@repo/ui/components/base/card";

type Consent = "all" | "necessary";

type ConsentContextValue = {
  consent: Consent | null;
  accept: () => void;
  reject: () => void;
  openBanner: () => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

const COOKIE_NAME = "bp_cookie_consent";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export function ConsentProvider({ children }: { children: ReactNode }) {
  // Cookie is read client-side after mount: reading it server-side (cookies())
  // would opt every route out of static generation. Undecided visitors see the
  // banner pop in right after hydration — the standard tradeoff.
  const [consent, setConsent] = useState<Consent | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const match = document.cookie.match(
      /(?:^|; )bp_cookie_consent=(all|necessary)(?:;|$)/,
    );
    if (match) {
      setConsent(match[1] as Consent);
    } else {
      setShowBanner(true);
    }
  }, []);

  const setConsentChoice = useCallback(
    (choice: Consent) => {
      setConsentCookie(choice);
      setConsent(choice);
      setShowBanner(false);
      // Revoking after acceptance must actually stop trackers: removing the
      // <script> does not unload the UET instance already holding history
      // listeners (enableAutoSpaTracking), so reload — the standard CMP
      // behavior on consent revocation.
      if (consent === "all" && choice === "necessary") {
        window.location.reload();
      }
    },
    [consent],
  );

  const accept = useCallback(() => setConsentChoice("all"), [setConsentChoice]);
  const reject = useCallback(
    () => setConsentChoice("necessary"),
    [setConsentChoice],
  );
  const openBanner = useCallback(() => setShowBanner(true), []);

  return (
    <ConsentContext.Provider value={{ consent, accept, reject, openBanner }}>
      {children}
      {showBanner ? (
        <ConsentBanner onAccept={accept} onReject={reject} />
      ) : null}
    </ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }
  return context;
}

function ConsentBanner({
  onAccept,
  onReject,
}: {
  onAccept: () => void;
  onReject: () => void;
}) {
  const t = useTranslations("cookieConsent");
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-2 sm:p-4 md:p-6">
      <Card className="mx-auto w-full max-w-4xl border bg-card/95 text-card-foreground shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <CardContent className="flex flex-col gap-2 p-3 sm:gap-3 sm:p-4 md:flex-row md:items-center md:justify-between md:gap-4 md:p-5">
          {/* On phones the full notice covered a quarter of the screen,
              hiding the CTAs; below `sm` it shrinks to one short line.
              `shortDescription` restates the last sentence of `description`
              in both locales: change one, change the other. */}
          <div className="space-y-2">
            <p className="hidden font-semibold text-foreground sm:block">
              {t("title")}
            </p>
            <p className="hidden text-sm text-muted-foreground sm:block">
              {t("description")}
            </p>
            <p className="text-xs text-muted-foreground sm:text-sm">
              <span className="sm:hidden">{t("shortDescription")} </span>
              <Link
                href="/privacy-policy"
                className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                {t("privacyLink")}
              </Link>
            </p>
          </div>
          {/* Identical styling on purpose: LGPD asks for equal prominence,
              so accept and reject get the same variant, size and weight —
              only the label differs. Anything that makes one read as the
              primary action (a filled CTA against an outline) is the nudge
              the rule exists to prevent. */}
          <div className="grid flex-shrink-0 grid-cols-2 gap-2 sm:flex sm:flex-row">
            <Button size="sm" onClick={onReject} className="order-2 sm:order-1">
              {t("onlyNecessary")}
            </Button>
            <Button size="sm" onClick={onAccept} className="order-1 sm:order-2">
              {t("acceptAll")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function setConsentCookie(value: Consent) {
  // `Secure` only over HTTPS: prod is HTTPS-only, but local dev serves HTTP
  // and Chromium drops Secure cookies there, which would lose the choice.
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${COOKIE_NAME}=${value}; max-age=${COOKIE_MAX_AGE}; path=/; samesite=lax${secure}`;
}
